import { FACILITY_TYPES, getFacilityById } from './facilityConfig';

// Availability status types
export const AVAILABILITY_STATUS = {
  AVAILABLE: 'available',
  UNAVAILABLE: 'unavailable',
  PARTIALLY_AVAILABLE: 'partially_available',
  UNKNOWN: 'unknown'
};

// Process API response and normalize data
export const processApiResponse = (apiResponse) => {
  if (!apiResponse || !apiResponse.facilities) {
    return null;
  }

  const processed = {
    lastUpdated: apiResponse.lastUpdated,
    facilities: {}
  };

  // Process each facility
  Object.entries(apiResponse.facilities).forEach(([facilityId, facilityData]) => {
    if (facilityData.error) {
      processed.facilities[facilityId] = {
        error: facilityData.error,
        status: AVAILABILITY_STATUS.UNKNOWN
      };
    } else {
      processed.facilities[facilityId] = {
        lastUpdated: facilityData.lastUpdated,
        days: processFacilityDays(facilityId, facilityData.days)
      };
    }
  });

  return processed;
};

// Process days data for a facility
const processFacilityDays = (facilityId, days) => {
  if (!days || !Array.isArray(days)) return [];
  
  const facility = getFacilityById(facilityId);
  if (!facility) return days;

  // Handle guest room daily availability differently
  if (facility.type === FACILITY_TYPES.DAILY) {
    return days.map(day => ({
      ...day,
      date: normalizeDate(day.date),
      status: day.status === 'AVAILABLE' ? AVAILABILITY_STATUS.AVAILABLE : AVAILABILITY_STATUS.UNAVAILABLE,
      available: day.status === 'AVAILABLE'
    }));
  }

  // Handle hourly facilities
  return days.map(day => ({
    ...day,
    date: normalizeDate(day.date),
    status: determineDayStatus(facility, day),
    slots: processSlots(facility, day.slots)
  }));
};

// Process time slots for a facility
const processSlots = (facility, slots) => {
  if (!slots || !Array.isArray(slots)) return [];
  
  return slots.map(slot => ({
    ...slot,
    displayTime: formatSlotTime(slot.time),
    status: slot.available ? AVAILABILITY_STATUS.AVAILABLE : AVAILABILITY_STATUS.UNAVAILABLE
  }));
};

// Determine overall status for a day
const determineDayStatus = (facility, day) => {
  if (!day.slots || day.slots.length === 0) {
    return AVAILABILITY_STATUS.UNKNOWN;
  }

  // Check the status field from the API
  const bookedSlots = day.slots.filter(slot => slot.status === 'booked');
  const pastDeadlineSlots = day.slots.filter(slot => slot.status === 'past-deadline');
  const availableSlots = day.slots.filter(slot => slot.status === 'available' || slot.available);
  
  // All slots are past deadline - check if any would have been available
  if (pastDeadlineSlots.length === day.slots.length) {
    const hasAnyAvailableTime = day.slots.some(slot => slot.available === true);
    return hasAnyAvailableTime ? AVAILABILITY_STATUS.AVAILABLE : AVAILABILITY_STATUS.UNAVAILABLE;
  }
  
  // If there are any booked slots
  if (bookedSlots.length > 0) {
    // Check if entire period is booked (excluding past-deadline slots)
    const activeSlotsCount = day.slots.filter(s => s.status !== 'past-deadline').length;
    if (bookedSlots.length === activeSlotsCount && activeSlotsCount > 0) {
      return AVAILABILITY_STATUS.UNAVAILABLE;
    } else {
      return AVAILABILITY_STATUS.PARTIALLY_AVAILABLE;
    }
  }
  
  // No bookings = available
  return AVAILABILITY_STATUS.AVAILABLE;
};

// Generate time slots for a facility
export const generateTimeSlots = (facility, date) => {
  const slots = [];
  
  if (facility.type === FACILITY_TYPES.HOURLY) {
    const [openHour, openMinute] = facility.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = facility.closeTime.split(':').map(Number);
    
    let currentHour = openHour;
    let currentMinute = openMinute;
    
    while (currentHour < closeHour || (currentHour === closeHour && currentMinute < closeMinute)) {
      const time = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
      slots.push({
        time,
        displayTime: formatSlotTime(time),
        available: true,
        status: AVAILABILITY_STATUS.AVAILABLE
      });
      
      // Increment by 30 minutes
      currentMinute += 30;
      if (currentMinute >= 60) {
        currentMinute = 0;
        currentHour++;
      }
    }
  } else if (facility.type === FACILITY_TYPES.DAILY) {
    // For daily facilities, just one slot per day
    slots.push({
      date: date,
      available: true,
      status: AVAILABILITY_STATUS.AVAILABLE
    });
  }
  
  return slots;
};

// Format slot time for display
export const formatSlotTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  return `${hours}:${minutes}`;
};

// Normalize date format
export const normalizeDate = (dateString) => {
  if (!dateString) return '';
  
  // Handle different date formats
  if (dateString.includes('/')) {
    return dateString; // Already in YYYY/MM/DD format
  } else if (dateString.includes('-')) {
    return dateString.replace(/-/g, '/'); // Convert YYYY-MM-DD to YYYY/MM/DD
  }
  
  return dateString;
};

// Format date for display
export const formatDateDisplay = (dateString) => {
  const date = new Date(dateString.replace(/\//g, '-'));
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  const weekday = weekdays[date.getDay()];
  
  return `${month}/${day} (${weekday})`;
};

// Get available time ranges for a day
export const getAvailableTimeRanges = (slots) => {
  if (!slots || slots.length === 0) return [];
  
  const ranges = [];
  let currentRange = null;
  
  slots.forEach((slot, index) => {
    // A slot is available if it's not booked and either has status 'available' or available: true
    const isAvailable = slot.status !== 'booked' && (slot.status === 'available' || slot.available === true);
    
    if (isAvailable) {
      if (!currentRange) {
        currentRange = { start: slot.time, end: slot.time };
      } else {
        currentRange.end = slot.time;
      }
    } else if (currentRange) {
      ranges.push(currentRange);
      currentRange = null;
    }
    
    // Handle last slot
    if (index === slots.length - 1 && currentRange) {
      ranges.push(currentRange);
    }
  });
  
  return ranges;
};

// Format time range for display
export const formatTimeRange = (range) => {
  if (!range || !range.start || !range.end) return '';
  
  // Add 30 minutes to end time for display
  const [endHours, endMinutes] = range.end.split(':').map(Number);
  let adjustedEndHours = endHours;
  let adjustedEndMinutes = endMinutes + 30;
  
  if (adjustedEndMinutes >= 60) {
    adjustedEndMinutes -= 60;
    adjustedEndHours++;
  }
  
  const adjustedEnd = `${adjustedEndHours.toString().padStart(2, '0')}:${adjustedEndMinutes.toString().padStart(2, '0')}`;
  
  return `${formatSlotTime(range.start)}-${formatSlotTime(adjustedEnd)}`;
};

// Get availability summary for a facility
export const getAvailabilitySummary = (facilityData) => {
  if (!facilityData || !facilityData.days) {
    return { totalDays: 0, availableDays: 0, partialDays: 0, unavailableDays: 0 };
  }
  
  const summary = {
    totalDays: facilityData.days.length,
    availableDays: 0,
    partialDays: 0,
    unavailableDays: 0
  };
  
  facilityData.days.forEach(day => {
    switch (day.status) {
      case AVAILABILITY_STATUS.AVAILABLE:
        summary.availableDays++;
        break;
      case AVAILABILITY_STATUS.PARTIALLY_AVAILABLE:
        summary.partialDays++;
        break;
      case AVAILABILITY_STATUS.UNAVAILABLE:
        summary.unavailableDays++;
        break;
      default:
        // Unknown status - do nothing
        break;
    }
  });
  
  return summary;
};

// Check if a slot is within operating hours
export const isWithinOperatingHours = (facility, time) => {
  if (!facility || !time) return false;
  
  const [slotHour, slotMinute] = time.split(':').map(Number);
  const slotMinutes = slotHour * 60 + slotMinute;
  
  if (facility.type === FACILITY_TYPES.HOURLY) {
    const [openHour, openMinute] = facility.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = facility.closeTime.split(':').map(Number);
    const openMinutes = openHour * 60 + openMinute;
    const closeMinutes = closeHour * 60 + closeMinute;
    
    return slotMinutes >= openMinutes && slotMinutes < closeMinutes;
  }
  
  return true; // Daily facilities don't have hourly restrictions
};

// Get next available slot
export const getNextAvailableSlot = (facilityData) => {
  if (!facilityData || !facilityData.days) return null;
  
  const now = new Date();
  const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  
  for (const day of facilityData.days) {
    const dayDate = new Date(day.date.replace(/\//g, '-'));
    
    // Skip past days
    if (dayDate < now && dayDate.toDateString() !== now.toDateString()) {
      continue;
    }
    
    // Check slots for this day
    if (day.slots) {
      for (const slot of day.slots) {
        // If it's today, skip past time slots
        if (dayDate.toDateString() === now.toDateString() && slot.time < currentTime) {
          continue;
        }
        
        if (slot.available) {
          return {
            date: day.date,
            time: slot.time,
            displayDate: formatDateDisplay(day.date),
            displayTime: formatSlotTime(slot.time)
          };
        }
      }
    }
  }
  
  return null;
};